import { Button, Tag, Intent } from "@blueprintjs/core"
import { useState } from "react"
import { FlexContainer, FlexContainerAndItem } from "../../../styled/flexbox.styled"
import { CollapseContainer, HeaderContainer, CountBadge } from "./CollapsibleTagList.styled"

// Type for items
export interface CollapsibleTagListItem {
  id: number
  name: string
  [key: string]: any // Allow additional properties
}

interface CollapsibleTagListProps<T extends CollapsibleTagListItem> {
  title: string
  items: T[]
  selectedItemIds?: number[]
  onItemToggle: (item: T) => void
  initialCollapsed?: boolean
  className?: string
  onClear?: () => void
}

export function CollapsibleTagList<T extends CollapsibleTagListItem>({
  title,
  items,
  selectedItemIds = [],
  onItemToggle,
  initialCollapsed = true,
  className,
  onClear,
}: CollapsibleTagListProps<T>) {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed)

  const toggleCollapse = () => setIsCollapsed(!isCollapsed)

  const isSelected = (item: T) => {
    return selectedItemIds.includes(item.id)
  }

  const handleClear = () => {
    onClear?.()
    setIsCollapsed(true)
  }

  return (
    <FlexContainer flexDirection="column" className={className}>
      <FlexContainerAndItem flexDirection="row" gap="10px" alignItems="center">
        <Button
          fill
          alignText="left"
          rightIcon={isCollapsed ? "chevron-right" : "chevron-down"}
          onClick={toggleCollapse}
        >
          <HeaderContainer>
            {title}
            {selectedItemIds.length > 0 && <CountBadge>({selectedItemIds.length})</CountBadge>}
          </HeaderContainer>
        </Button>
        {onClear && selectedItemIds.length > 0 && (
          <Button icon="filter-remove" minimal outlined onClick={handleClear} />
        )}
      </FlexContainerAndItem>

      {!isCollapsed && (
        <CollapseContainer flexDirection="row" flexWrap="wrap" gap="10px">
          {items.map(item => (
            <Tag
              key={item.id}
              interactive
              active={isSelected(item)}
              onClick={() => onItemToggle(item)}
              intent={isSelected(item) ? Intent.PRIMARY : Intent.NONE}
            >
              {item.name}
            </Tag>
          ))}
        </CollapseContainer>
      )}
    </FlexContainer>
  )
}

export default CollapsibleTagList
